import { useState } from "react";
import { CrownSimple, Lightning, ArrowClockwise, Barbell, UsersFour, ArrowLeft, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TIER_COLORS = { bronze: "#B87333", gold: "#FFD700", silver: "#C0C0C0" };
const ORDER = ["bronze", "gold", "silver"];

export const BUSINESS_TYPES = [
  {
    id: "pt",
    icon: Barbell,
    title: "Personal training",
    blurb: "Mostly private 1:1 clients, with the odd group session you can share with your client base and promote to the public.",
    points: ["Client scheduling, direct debit & invoicing", "Progress, habits, check-ins, AI coach", "Optional Group Sessions add-on (+£20/mo)"],
  },
  {
    id: "group",
    icon: UsersFour,
    title: "Group sessions",
    blurb: "Class-first business — Pilates, yoga, bootcamps — with the odd private session on the side.",
    points: ["Public class schedule, bookings & class packs", "Instructor team logins", "Full coach portal for unlimited private clients"],
  },
];

export const PlanGate = ({ status, onRefresh }) => {
  const { refresh: refreshUser } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [savingType, setSavingType] = useState(null);
  const [withAddon, setWithAddon] = useState(false);
  const [picking, setPicking] = useState(false);
  const failed = status.status && !["active", "trialing"].includes(status.status);
  const type = status.business_type;

  const chooseType = async (id) => {
    setSavingType(id);
    try {
      await api.post("/platform/billing/business-type", { business_type: id });
      await Promise.all([onRefresh(), refreshUser()]);
      setPicking(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSavingType(null); }
  };

  const choose = async (planId) => {
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/platform/billing/checkout", {
        plan: planId, origin_url: window.location.origin, with_group_addon: planId !== "group" && withAddon,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoadingPlan(null);
    }
  };

  const managePayment = async () => {
    try {
      const { data } = await api.post("/platform/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open billing portal");
    }
  };

  const recheck = async () => {
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  };

  if (!type || picking) {
    return (
      <div className="max-w-[1000px] mx-auto px-4 py-12" data-testid="business-type-gate">
        <div className="text-center mb-10">
          <div className="tracker text-brand mb-3">[ Step 1 of 2 ]</div>
          <h1 className="font-display text-4xl sm:text-5xl mb-3">What kind of business are you?</h1>
          <p className="text-zinc-400 text-sm max-w-md mx-auto">This shapes your plan options and how your portal is laid out. You can change it later in Settings.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {BUSINESS_TYPES.map((b) => {
            const Icon = b.icon;
            const selected = type === b.id;
            return (
              <button
                key={b.id}
                onClick={() => chooseType(b.id)}
                disabled={savingType !== null}
                className={`card-flat p-8 text-left flex flex-col gap-4 hover:border-brand/60 transition-colors disabled:opacity-60 ${selected ? "border-brand" : ""}`}
                data-testid={`business-type-${b.id}`}
              >
                <Icon size={32} weight="duotone" className="text-brand" />
                <div>
                  <div className="font-display text-3xl mb-2">{b.title}</div>
                  <p className="text-zinc-400 text-sm">{b.blurb}</p>
                </div>
                <ul className="space-y-1.5 mt-auto">
                  {b.points.map((p) => (
                    <li key={p} className="text-xs text-zinc-300 flex items-center gap-2"><Check size={12} weight="bold" className="text-brand-light" /> {p}</li>
                  ))}
                </ul>
                <span className="tracker text-[10px] text-brand-light mt-2">{savingType === b.id ? "Saving…" : selected ? "Current choice →" : "Choose →"}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  const plans = ORDER.map((id) => status.plans.find((p) => p.id === id)).filter(Boolean);
  const addonPrice = (status.group_addon_price || 2000) / 100;

  return (
    <div className="max-w-[1000px] mx-auto px-4 py-12" data-testid="plan-gate">
      <div className="text-center mb-10">
        <div className="tracker text-brand mb-3">[ {failed ? "Subscription issue" : "Step 2 of 2"} ]</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3">{failed ? "Payment needed." : "Choose your plan."}</h1>
        <p className="text-zinc-400 text-sm max-w-md mx-auto">
          {failed
            ? `Your subscription is ${status.status.replace("_", " ")} — update your payment method or pick a plan to unlock your portal.`
            : type === "group"
              ? "One plan for group-first businesses — classes, bookings, packs, instructors and unlimited private clients."
              : "Pick a plan to unlock your coaching portal. Cancel anytime from Settings."}
        </p>
        {failed && (
          <button onClick={managePayment} className="btn-primary mt-4 !py-2 !px-5 !text-xs" data-testid="plan-gate-manage-billing">
            Update payment method
          </button>
        )}
        <button onClick={() => setPicking(true)} className="tracker text-[10px] text-zinc-500 hover:text-zinc-300 mt-4 inline-flex items-center gap-1" data-testid="plan-gate-switch-type">
          <ArrowLeft size={11} /> {type === "group" ? "Group sessions business" : "Personal training business"} · change
        </button>
      </div>

      {type === "group" ? (
        <div className="card-flat p-8 sm:p-10 max-w-2xl mx-auto relative border-brand/50" data-testid="plan-card-group">
          <div className="absolute -top-3 left-6 tracker bg-brand text-white px-2 py-1 text-[10px] font-bold">
            <UsersFour size={10} weight="fill" className="inline -mt-0.5 mr-1" />Group-first
          </div>
          <div className="tracker mb-2 text-brand-light">{status.group_plan.label.toUpperCase()}</div>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-display text-6xl text-brand">£{status.group_plan.amount / 100}</span>
            <span className="tracker text-zinc-500">/ mo</span>
          </div>
          <p className="text-zinc-400 text-sm mb-3">{status.group_plan.blurb}</p>
          <p className="text-xs mb-8 flex items-center gap-1.5 text-brand-light">
            <Lightning size={12} weight="fill" /> {status.group_plan.limit}
          </p>
          <button
            onClick={() => choose("group")}
            disabled={loadingPlan !== null}
            className="btn-primary justify-center w-full disabled:opacity-50"
            data-testid="plan-choose-group"
          >
            {loadingPlan === "group" ? "Opening checkout…" : "Start Group Sessions"}
          </button>
        </div>
      ) : (
        <>
          <label className="card-flat p-5 mb-8 flex items-center justify-between gap-4 flex-wrap cursor-pointer max-w-2xl mx-auto" data-testid="plan-addon-row">
            <div className="flex items-center gap-3">
              <UsersFour size={22} weight="duotone" className="text-brand-light" />
              <div>
                <div className="font-display text-lg">Add Group Sessions</div>
                <p className="text-zinc-500 text-xs">Public class schedule, bookings & packs — share with clients and promote to the public.</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-display text-2xl">+£{addonPrice}<span className="tracker text-zinc-500 text-[10px]"> / mo</span></span>
              <input type="checkbox" checked={withAddon} onChange={(e) => setWithAddon(e.target.checked)} className="h-5 w-5 accent-[var(--brand)]" data-testid="plan-addon-toggle" />
            </div>
          </label>

          <div className="grid md:grid-cols-3 gap-5 md:items-end">
            {plans.map((p) => {
              const color = TIER_COLORS[p.id];
              const isGold = p.id === "gold";
              const total = (p.amount + (withAddon ? (status.group_addon_price || 2000) : 0)) / 100;
              return (
                <div
                  key={p.id}
                  className={`card-flat p-7 flex flex-col relative ${isGold ? "border-[#FFD700]/50 md:scale-[1.03] md:-translate-y-4 shadow-2xl shadow-[#FFD700]/10" : p.id === "silver" ? "md:-translate-y-2" : ""}`}
                  data-testid={`plan-card-${p.id}`}
                >
                  {isGold && (
                    <div className="absolute -top-3 left-6 tracker bg-[#FFD700] text-[#0A0A0A] px-2 py-1 text-[10px] font-bold">
                      <CrownSimple size={10} weight="fill" className="inline -mt-0.5 mr-1" />Most popular
                    </div>
                  )}
                  <div className="tracker mb-2" style={{ color }}>{p.label.toUpperCase()}</div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className={`font-display ${isGold ? "text-6xl text-brand" : "text-5xl"}`} data-testid={`plan-price-${p.id}`}>£{total}</span>
                    <span className="tracker text-zinc-500">/ mo</span>
                  </div>
                  {withAddon && <p className="tracker text-[9px] text-brand-light mb-2">£{p.amount / 100} plan + £{addonPrice} group sessions</p>}
                  <p className="text-zinc-400 text-sm mb-2">{p.blurb}</p>
                  <p className="text-xs mb-6 flex items-center gap-1.5" style={{ color }}>
                    <Lightning size={12} weight="fill" /> {p.limit}
                  </p>
                  <button
                    onClick={() => choose(p.id)}
                    disabled={loadingPlan !== null}
                    className={`${isGold ? "btn-primary" : "btn-ghost"} justify-center mt-auto disabled:opacity-50`}
                    data-testid={`plan-choose-${p.id}`}
                  >
                    {loadingPlan === p.id ? "Opening checkout…" : `Take the ${p.label}`}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="text-center mt-8">
        <button onClick={recheck} className="tracker text-[10px] text-zinc-500 hover:text-zinc-300" data-testid="plan-gate-recheck">
          <ArrowClockwise size={11} className={`inline mr-1 -mt-0.5 ${refreshing ? "animate-spin" : ""}`} />
          Already paid? Re-check status
        </button>
      </div>
    </div>
  );
};
